'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from 'react';
const COOKIE_PREFIX = 'pp_tooltip_';
const SESSION_STORAGE_PREFIX = 'pp_tooltip_';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/** Number of times a tooltip is auto shown before it stays hidden for good. */
export const MAX_AUTO_TOOLTIP_VIEWS = 5;
const getCookieName = (key: string): string => `${COOKIE_PREFIX}${encodeURIComponent(key)}`;
const getSessionStorageKey = (key: string): string => `${SESSION_STORAGE_PREFIX}${key}`;
const readViewCount = (key: string): number => {
  const name = getCookieName(key);
  const entry = document.cookie.split(';').find(cookie => cookie.trim().startsWith(`${name}=`));
  if (!entry) {
    return 0;
  }
  const count = Number.parseInt(entry.trim().slice(name.length + 1), 10);
  return Number.isFinite(count) && count > 0 ? count : 0;
};
const writeViewCount = (key: string, count: number): void => {
  // Browsers reject `Secure` cookies on http, which local dev may run on.
  const secure = location.protocol === 'https:' ? '; Secure' : '';

  // The CookieStore API is not available in all browsers we support.
  // eslint-disable-next-line unicorn/no-document-cookie
  document.cookie = `${getCookieName(key)}=${count}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
};
const hasBeenShownThisSession = (key: string): boolean => sessionStorage.getItem(getSessionStorageKey(key)) === 'true';
const markShownThisSession = (key: string): void => {
  sessionStorage.setItem(getSessionStorageKey(key), 'true');
};
type UseAutoTooltipResult = {
  isOpen: boolean;
  isAutoShown: boolean;
  handleOpenChange: (isNextOpen: boolean) => void;
  ref: (element: HTMLElement | null) => void;
};

/**
 * Auto shows a tooltip when its trigger scrolls into view, a limited number of times
 * and at most once per browser session.
 *
 * Once auto shown it stays open until the user clicks or presses Escape, so scrolling
 * and pointer movement do not close it.
 *
 * Session storage guards against showing it twice in the same session, while a cookie
 * caps the total number of times it is shown across sessions.
 *
 * @param key Unique identifier for the tooltip.
 * @param maxViews Maximum number of times the tooltip is auto shown across sessions.
 * @returns Controlled open state, whether it is currently auto shown, change handler and the ref to put on the tooltip trigger.
 */
export function useAutoTooltip(key, t0) {
  const $ = _c(15);
  const maxViews = t0 === undefined ? MAX_AUTO_TOOLTIP_VIEWS : t0;
  const [isOpen, setIsOpen] = useState(false);
  const [isAutoShown, setIsAutoShown] = useState(false);
  const [trigger, setTrigger] = useState();
  let t1;
  let t2;
  if ($[0] !== key || $[1] !== maxViews || $[2] !== trigger) {
    t1 = () => {
      if (!trigger || hasBeenShownThisSession(key) || readViewCount(key) >= maxViews) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (!entries.some(_temp)) {
          return;
        }
        observer.disconnect();
        if (hasBeenShownThisSession(key)) {
          return;
        }
        markShownThisSession(key);
        writeViewCount(key, readViewCount(key) + 1);
        setIsOpen(true);
        setIsAutoShown(true);
      });
      observer.observe(trigger);
      return () => {
        observer.disconnect();
      };
    };
    t2 = [key, maxViews, trigger];
    $[0] = key;
    $[1] = maxViews;
    $[2] = trigger;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] !== isAutoShown) {
    t3 = () => {
      if (!isAutoShown) {
        return;
      }
      const dismiss = () => {
        setIsAutoShown(false);
        setIsOpen(false);
      };
      const handleKeyDown = event => {
        if (event.key === "Escape") {
          dismiss();
        }
      };
      document.addEventListener("pointerdown", dismiss);
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("pointerdown", dismiss);
        document.removeEventListener("keydown", handleKeyDown);
      };
    };
    t4 = [isAutoShown];
    $[5] = isAutoShown;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== isAutoShown) {
    t5 = isNextOpen => {
      if (isAutoShown && !isNextOpen) {
        return;
      }
      setIsOpen(isNextOpen);
    };
    $[8] = isAutoShown;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleOpenChange = t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = element => {
      setTrigger(element ?? undefined);
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const ref = t6;
  let t7;
  if ($[11] !== handleOpenChange || $[12] !== isAutoShown || $[13] !== isOpen) {
    t7 = {
      isOpen,
      isAutoShown,
      handleOpenChange,
      ref
    };
    $[11] = handleOpenChange;
    $[12] = isAutoShown;
    $[13] = isOpen;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
function _temp(entry) {
  return entry.isIntersecting;
}